<script setup lang="ts">
import { computed, ref } from 'vue'

import { contentfulUrl } from '@internal/format-contentful'
import Button from '@satch-ui/components/button/Button.vue'
import TeaserCardItemContent from '@storefront-satch/components/molecules/teaser-card/TeaserCardItemContent.vue'
import TeaserCardItemMedia from '@storefront-satch/components/molecules/teaser-card/TeaserCardItemMedia.vue'
import type {
  TeaserCardItemAdditionalProps,
  TeaserCardItemProps,
} from '@storefront-satch/components/molecules/teaser-card/types'
import { teaserCardItemVariants } from '@storefront-satch/components/molecules/teaser-card/variants.ts'
import { clsx } from 'clsx'

import { ArrowRightIcon, PlayIcon } from '@fond-of/satch-icons'

const props = defineProps<TeaserCardItemProps & TeaserCardItemAdditionalProps>()
const isVideoPlaying = ref<boolean>(false)

const hasVideo = computed(
  () =>
    props.media?.videoMobile?.url ||
    props.media?.videoTablet?.url ||
    props.media?.videoDesktop?.url
)

const isVideo = computed(() => !props.media?.imageMobile)
const videoHasControls = computed(() => props.media?.videoControls)
const videoIsAutoplay = computed(() => props.media?.videoAutoplay)
const hasLinkAndLinkText = computed(() => !!(props.link && props.linkText))
const width = computed(() => props.width ?? 'single')
const type = computed(() => props.type ?? 'half')
const groupType = computed(() => props.groupType ?? 'grid')
const showPlayIcon = computed(
  () => hasVideo.value && !videoIsAutoplay.value && props.type === 'full'
)

const showVideoWithLinkButton = computed(() => {
  return (
    (hasVideo.value &&
      type.value === 'full' &&
      videoIsAutoplay.value &&
      !videoHasControls.value &&
      hasLinkAndLinkText.value) ||
    (type.value === 'half' && hasLinkAndLinkText.value)
  )
})

const showVideoWithPlayButton = computed(
  () =>
    hasVideo.value &&
    type.value === 'full' &&
    !videoIsAutoplay.value &&
    !videoHasControls.value
)

const isLinkElement = computed(() => {
  return props.link || showVideoWithLinkButton.value
})
</script>

<template>
  <div
    :id="props.sys.id"
    :class="
      clsx(
        'relative isolate',
        teaserCardItemVariants({
          groupType: props.groupType,
          size: props.size,
          type: props.type,
          width: props.width,
        })
      )
    "
    :data-cfid="props.sys.id"
    data-label="Teaser Card Item"
    :data-title="props.internalTitle"
  >
    <component
      :is="isLinkElement ? 'a' : 'div'"
      class="relative flex flex-wrap content-start"
      :href="contentfulUrl(props.link)"
      :data-gtm-attr="
        isLinkElement ? 'link_teaser-card-item-medium' : undefined
      "
      :data-gtm-label="isLinkElement ? props.internalTitle : undefined"
      :aria-label="
        isLinkElement
          ? props.linkAriaLabel?.trim() || props.linkText?.trim() || undefined
          : undefined
      "
    >
      <span
        v-if="props.showNumber"
        class="font-sans-semi-bold absolute left-5 top-2.5 z-10 flex size-8 min-h-0 content-center items-center justify-center rounded-[50%] border-[2px] border-white text-white"
      >
        {{ props.number }}
      </span>
      <TeaserCardItemMedia
        v-if="props.media"
        :media="props.media"
        size="large"
        :type="type"
        :width="width"
        :group-type="groupType"
        :show-play-button="false"
        :media-aspect-ratio="props.mediaAspectRatio"
        :media-fit="props.mediaFit"
        @update:video-playing="
          (state) => {
            isVideoPlaying = state
          }
        "
      />

      <TeaserCardItemContent
        v-bind="props.content"
        :hide-content="isVideoPlaying && isVideo"
        :is-clickable="hasLinkAndLinkText && !isVideoPlaying"
        headline-size="large"
        :content-style="props.contentStyle"
        :link-style="props.linkStyle"
        :text-size="props.type !== 'full' ? 'extraSmall' : 'medium'"
        :is-text-compact="props.groupType === 'slider'"
        :class="[
          'max-h-full w-full px-5',
          {
            'sm:pb-7.5 xl:pb-12.5 absolute inset-0 mb-[0.25em] flex flex-col justify-end p-5 pr-2.5 xl:px-10 xl:pt-5':
              props.type === 'full',
            'md:w-2/5': props.type === 'half' && props.width === 'double',
            'md:w-1/2': props.type !== 'half' && props.width === 'double',
          },
        ]"
      >
        <template #contentFooter>
          <Button
            v-if="
              (hasLinkAndLinkText && !hasVideo) ||
              showVideoWithLinkButton ||
              showVideoWithPlayButton
            "
            as="span"
            class="mt-2.5 w-fit"
            :variant="props.linkStyle || 'outlineWhite'"
            :hover="false"
          >
            {{ props.linkText }}
            <PlayIcon
              v-if="showPlayIcon"
              class="size-4"
            />
            <ArrowRightIcon
              v-if="!showPlayIcon"
              class="size-4 stroke-2"
            />
          </Button>
        </template>
      </TeaserCardItemContent>
    </component>
  </div>
</template>
